import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, Building2, Landmark,
  Users, PieChart, Globe, BarChart3, Shield,
  ArrowLeftRight, ChevronLeft, ChevronRight, Database, LogOut
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/portfolio", label: "Portfolio", icon: Briefcase },
  { path: "/properties", label: "Properties", icon: Building2 },
  { path: "/funds", label: "Funds", icon: Landmark },
  { path: "/managers", label: "Managers", icon: Users },
  { path: "/sectors", label: "Sectors", icon: PieChart },
  { path: "/geography", label: "Geography", icon: Globe },
  { path: "/benchmarks", label: "Benchmarks", icon: BarChart3 },
  { path: "/risk", label: "Risk", icon: Shield },
  { path: "/cashflow", label: "Cashflow", icon: ArrowLeftRight },
  { path: "/data", label: "Data Mgmt", icon: Database },
];

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <div
      className="erp-sidebar flex flex-col border-r border-border bg-card h-screen"
      style={{ width: collapsed ? 64 : 240 }}
      data-testid="sidebar"
    >
      <div className="flex items-center h-14 px-4 border-b border-border shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-primary flex items-center justify-center">
              <span className="text-xs font-bold text-primary-foreground font-data">PI</span>
            </div>
            <span className="text-sm font-semibold tracking-tight">Portfolio Intelligence</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="ml-auto p-1 rounded-sm hover:bg-secondary transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <ScrollArea className="flex-1 py-2">
        <nav className="flex flex-col gap-0.5 px-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-sm text-sm ${
                  isActive
                    ? "active bg-primary/10 text-primary border-r-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="border-t border-border p-3 shrink-0">
        {!collapsed ? (
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground truncate min-w-0">
              <div className="font-medium text-foreground truncate">{user?.name}</div>
              <div className="font-data opacity-60 truncate">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded-sm hover:bg-secondary transition-colors shrink-0"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut size={15} className="text-muted-foreground" />
            </button>
          </div>
        ) : (
          <button
            onClick={logout}
            className="p-1.5 rounded-sm hover:bg-secondary transition-colors mx-auto block"
            data-testid="logout-btn"
            title="Sign out"
          >
            <LogOut size={15} className="text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}
